/**
 * The Missions landing view: the backlog (tree, board, table, document, defects) with filters
 * and saved views in the URL, the missions list, and the review queue of AI proposals and
 * suggested defects.
 */
import { Bug, Check, ChevronDown, Columns3, FileText, FolderGit2, ListTree, Plus, Rocket, SearchX, Sparkles, Table2, Target, Upload } from '@orgus/ui/icons';
import { useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { BACKLOG_VIEW_LABEL, ITEM_STATUS_LABEL } from '@orgus/protocol';
import type { AiJob, BacklogItem, BacklogViewKind, Id, ItemStatus, MissionsState, Project, QualityIssue, SavedView } from '@orgus/protocol';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, EmptyState, GroupChevron, Panel, Pressable, Segmented, Skeleton, StatStrip, StatTile, plural, useNow } from '@orgus/ui';
import { DefectsView } from '../requirements/DefectsView.tsx';
import { DocView } from '../requirements/DocView.tsx';
import { FilterBar } from '../requirements/FilterBar.tsx';
import { ReviewQueue } from '../requirements/ReviewQueue.tsx';
import { TableView } from '../requirements/TableView.tsx';
import { filterCount, filterReq, labelsInUse, ownersInUse, readFilter, readSort, readView, viewParams, writeFilter } from '../requirements/model.ts';
import type { ReqFilter } from '../requirements/model.ts';
import { useOrgus } from '../../lib/store.ts';
import { BacklogBulk } from './BacklogBulk.tsx';
import { autopilotTarget, shownAutopilots } from './autopilot.ts';
import { AutopilotBar } from './AutopilotBar.tsx';
import { BacklogTree } from './BacklogTree.tsx';
import { ReviewMissions } from './ReviewMissions.tsx';
import { Board } from './Board.tsx';
import { MissionList } from './Lists.tsx';
import { openProposals } from './glance.ts';
import { BOARD_COLUMNS, filterMissions, reviewQueue, scopeCounts } from './model.ts';
import type { MissionRow, MissionScope } from './model.ts';

export type HomeTab = 'backlog' | 'missions' | 'review';

export function readHomeParams(p: URLSearchParams) {
  const t = p.get('tab');
  const tab: HomeTab = t === 'missions' || t === 'review' ? t : 'backlog';
  const scopeRaw = p.get('scope');
  const scope: MissionScope = scopeRaw === 'closed' || scopeRaw === 'all' ? scopeRaw : 'active';
  return {
    tab,
    view: readView(p),
    scope,
    filter: readFilter(p),
    sort: readSort(p.get('sort')),
    projectId: p.get('project'),
    itemId: p.get('item'),
    proposalId: p.get('proposal'),
    docId: p.get('doc'),
    savedId: p.get('saved'),
    showClosed: p.get('closed') === '1',
  };
}

export function HomeSkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading missions">
      <StatStrip label="Loading mission counts" className="lg:grid-cols-4">
        {['Needs you', 'Under way', 'In review', 'Done this week'].map((l) => (
          <StatTile key={l} label={l} value={0} loading />
        ))}
      </StatStrip>
      <div className="flex h-filter-bar items-center gap-2">
        <Skeleton className="h-control-sm w-64" />
        <Skeleton className="h-control-sm w-56" />
        <Skeleton className="ml-auto h-control-sm w-40" />
      </div>
      <Panel flush>
        <div className="flex flex-col gap-1 p-2">
          {Array.from({ length: 9 }, (_, i) => (
            <div key={i} className="flex h-row items-center gap-3 px-4">
              <Skeleton className="size-4" />
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3" style={{ width: `${30 + ((i * 13) % 30)}%` }} />
              <Skeleton className="ml-auto h-3 w-20" />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

const VIEW_ICON: Record<BacklogViewKind, typeof ListTree> = { tree: ListTree, board: Columns3, table: Table2, doc: FileText, defects: Bug };
const VIEW_KEY: Record<BacklogViewKind, string> = { tree: 't', board: 'b', table: 'l', doc: 'd', defects: 'f' };

export function MissionsHome({
  state,
  rows,
  projects,
  projectName,
  issues,
  jobs,
  onOpenItem,
  onNewItem,
  onImport,
  onStartItem,
  onStartMany,
  onRetry,
  onStartSequence,
  onNewMission,
  onMove,
  onReview,
  onAiStarted,
  searchRef,
}: {
  state: MissionsState;
  rows: MissionRow[];
  projects: Project[];
  projectName: (id: Id) => string;
  issues: ReadonlyMap<Id, QualityIssue[]>;
  jobs: readonly AiJob[];
  onOpenItem: (id: Id) => void;
  onNewItem: (draft?: { status?: ItemStatus; parentId?: Id; kind?: BacklogItem['kind']; projectId?: Id }) => void;
  onImport: () => void;
  onStartItem: (item: BacklogItem) => void;
  /** Start one mission for each of several items, with one choice of machine and options. */
  onStartMany?: (items: BacklogItem[]) => void;
  onRetry?: (row: MissionRow) => void;
  onStartSequence: (item: BacklogItem) => void;
  onNewMission: () => void;
  onMove: (item: BacklogItem, status: ItemStatus) => void;
  onReview: (proposalId: Id) => void;
  onAiStarted: (jobId: Id) => void;
  searchRef?: Ref<HTMLInputElement>;
}) {
  const navigate = useNavigate();
  const hasMachine = useOrgus((s) => Object.keys(s.machines).length > 0);
  // First run: a project lives on a machine, so with none the way forward starts there.
  const firstStep = hasMachine ? { to: '/projects', label: 'Go to Projects' } : { to: '/getting-started?step=machine', label: 'Connect a machine' };
  const [params, setParams] = useSearchParams();
  const home = readHomeParams(params);
  const { tab, view, scope, filter, sort, projectId, itemId } = home;
  // The board groups cards into one lane per epic unless the person asked for one flat board.
  const lanes = params.get('lanes') !== 'none';
  const links = state.links ?? [];
  const views: SavedView[] = state.views ?? [];
  const setParam = (k: string, v: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v) n.set(k, v);
        else n.delete(k);
        if (k !== 'item' && k !== 'proposal' && k !== 'doc' && k !== 'tab') n.delete('saved');
        return n;
      },
      { replace: true },
    );
  const setFilter = (f: ReqFilter) => setParams((p) => {
    const n = writeFilter(p, f);
    n.delete('saved');
    return n;
  }, { replace: true });

  const inProject = useMemo(() => state.items.filter((i) => !projectId || i.projectId === projectId), [state.items, projectId]);
  const matched = useMemo(() => filterReq(state.items, filter, issues), [state.items, filter, issues]);
  const labels = useMemo(() => labelsInUse(inProject), [inProject]);
  const owners = useMemo(() => ownersInUse(inProject), [inProject]);
  const columns = useMemo(
    () =>
      BOARD_COLUMNS.map((status) => ({ status, title: ITEM_STATUS_LABEL[status], items: matched.filter((i) => i.status === status) })).filter(
        (c) => c.status !== 'triage' || c.items.length > 0,
      ),
    [matched],
  );
  const missionRows = useMemo(() => filterMissions(rows, 'all', projectId), [rows, projectId]);
  const now = useNow(5000);
  const counts = useMemo(() => scopeCounts(rows, projectId), [rows, projectId]);
  const queue = useMemo(() => reviewQueue(rows, projectId), [rows, projectId]);
  // The same count the In review tile shows (glance.ts), so "review" is one number in both places.
  const aiToReview = openProposals(state.proposals, state.signals, projectId);
  const toReview = queue.length + aiToReview;
  const [showAi, setShowAi] = useState<boolean | null>(null);
  const [tableDeleting, setTableDeleting] = useState<BacklogItem[] | null>(null);
  // The AI proposals and suggested defects fold under the missions queue; open when they are all there is.
  const aiOpen = showAi ?? (queue.length === 0 || !!home.proposalId);
  const backlogCount = inProject.filter((i) => i.status !== 'dropped').length;
  const showProject = !projectId && projects.length > 1;
  const narrowed = filterCount(filter) > 0;
  const docRef = useRef<HTMLDivElement>(null);
  // Autopilot is per project: the chosen one (or the only one), else every project set up for it.
  const pilotProject = autopilotTarget(projectId, projects);
  const pilots = shownAutopilots(state, pilotProject);

  // One quiet menu, not a bordered select: the project scopes the whole page.
  const current = projects.find((p) => p.id === projectId);
  const projectMenu =
    projects.length > 1 || current ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" icon={<FolderGit2 />} iconRight={<ChevronDown />} aria-label={`Project: ${current?.name ?? 'All projects'}`} className="max-w-48">
            <span className="truncate">{current?.name ?? 'All projects'}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {[{ id: '', name: 'All projects' }, ...projects].map((p) => (
            <DropdownMenuItem key={p.id || 'all'} icon={(projectId ?? '') === p.id ? <Check /> : <span className="size-4" />} onSelect={() => setParam('project', p.id || null)}>
              {p.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  /** The page's three surfaces in one small control, with quiet counts: no tab bar of its own. */
  const tabSwitch = (
    <Segmented
      label="Missions views"
      value={tab}
      onChange={(t) => setParam('tab', t === 'backlog' ? null : t)}
      options={[
        { value: 'backlog', label: 'Backlog', count: backlogCount },
        { value: 'missions', label: 'Missions', count: counts.active },
        { value: 'review', label: 'Review', count: toReview },
      ]}
    />
  );

  const firstUse = (
    <Panel>
      <EmptyState
        size="page"
        icon={<Target />}
        title="No requirements yet"
        description={
          projects.length
            ? 'Add epics and stories, or import a spec, then start a mission on any of them.'
            : hasMachine
              ? 'Requirements belong to a project. Add a project first.'
              : 'Requirements belong to a project. Connect a machine, then add one.'
        }
        action={
          projects.length ? (
            <span className="flex flex-wrap justify-center gap-2">
              <Button variant="primary" icon={<Plus />} onClick={() => onNewItem()} shortcut={['c']}>
                New item
              </Button>
              <Button variant="secondary" icon={<Upload />} onClick={onImport} shortcut={['i']}>
                Import a document
              </Button>
            </span>
          ) : (
            <Button variant="primary" onClick={() => navigate(firstStep.to)}>
              {firstStep.label}
            </Button>
          )
        }
      />
    </Panel>
  );

  const filteredEmpty = (
    <Panel>
      <EmptyState
        icon={<SearchX />}
        title="No items match"
        description={projectId ? `Nothing in ${projectName(projectId)} matches these filters.` : 'Nothing matches these filters.'}
        action={
          <Button variant="secondary" onClick={() => setFilter({ projectId })}>
            Clear filters
          </Button>
        }
      />
    </Panel>
  );

  const pickView = (v: BacklogViewKind) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v === 'tree') n.delete('view');
        else n.set('view', v);
        n.delete('saved');
        return n;
      },
      { replace: true },
    );
  // List, Board, Table in one small control; the document and the defects views live in the Views menu.
  const viewSwitch = (
    <Segmented
      label="Backlog view"
      value={view}
      onChange={pickView}
      options={(['tree', 'board', 'table'] as const).map((v) => {
        const I = VIEW_ICON[v];
        return { value: v, label: `${v === 'tree' ? 'List' : BACKLOG_VIEW_LABEL[v]} (${VIEW_KEY[v]})`, icon: <I />, iconOnly: true };
      })}
    />
  );
  const builtinViews = (['doc', 'defects'] as const).map((v) => {
    const I = VIEW_ICON[v];
    return { id: v, label: v === 'doc' ? 'Read as a document' : 'Defects', icon: <I />, shortcut: [VIEW_KEY[v]], active: view === v, onSelect: () => pickView(v) };
  });

  let backlog;
  if (!state.items.length) backlog = firstUse;
  else if (view === 'tree') backlog = matched.length || !narrowed ? <BacklogTree items={state.items} filter={filter} rows={rows} issues={issues} selectedId={itemId} owners={owners} onOpen={onOpenItem} onAddChild={(p) => onNewItem({ parentId: p.id, projectId: p.projectId, kind: p.kind === 'epic' ? 'feature' : p.kind === 'feature' ? 'story' : 'task' })} onStart={onStartItem} onStartMany={onStartMany} onStartSequence={onStartSequence} onOpenMission={(id) => navigate(`/missions/${encodeURIComponent(id)}`)} projectName={projectName} showProject={showProject} /> : filteredEmpty;
  else if (view === 'board') backlog = matched.length ? <Board columns={columns} allItems={state.items} rows={rows} issues={issues} projectName={projectName} showProject={showProject} selectedId={itemId} lanes={lanes} onOpen={onOpenItem} onMove={onMove} onStart={onStartItem} onRetry={onRetry} onStartSequence={onStartSequence} onAdd={(status) => onNewItem({ status })} /> : filteredEmpty;
  else if (view === 'table') backlog = matched.length ? <TableView items={matched} issues={issues} sort={sort} onSort={(s) => setParam('sort', `${s.key}:${s.dir}`)} selectedId={itemId} onOpen={onOpenItem} projectName={projectName} showProject={showProject} renderBulk={(picked, clear, selectAll) => <BacklogBulk picked={picked} allItems={state.items} rows={rows} owners={owners} total={matched.length} onSelectAll={selectAll} onClear={clear} onStart={onStartItem} onStartMany={onStartMany} deleting={tableDeleting} onDeleting={setTableDeleting} />} /> : filteredEmpty;
  else if (view === 'doc') backlog = <div ref={docRef}><DocView items={state.items} links={links} projectId={projectId} rootId={home.docId} onRoot={(id) => setParam('doc', id)} onOpen={onOpenItem} leading={tabSwitch} end={<>{projectMenu}{viewSwitch}</>} /></div>;
  else
    backlog = (
      <DefectsView
        items={matched}
        allItems={state.items}
        links={links}
        rows={rows}
        issues={issues}
        signals={state.signals ?? []}
        showClosed={home.showClosed}
        onShowClosed={(v) => setParam('closed', v ? '1' : null)}
        selectedId={itemId}
        onOpen={onOpenItem}
        onReviewSignals={() => setParam('tab', 'review')}
        onNewDefect={() => onNewItem({ kind: 'defect' })}
      />
    );

  const plainBar = (
    <div className="flex min-h-filter-bar flex-wrap items-center gap-2" role="toolbar" aria-label="Missions views">
      {tabSwitch}
      <div className="ml-auto flex items-center gap-2">{projectMenu}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {state.items.length > 0 && pilots.map((a) => <AutopilotBar key={a.projectId} status={a} projectName={pilotProject ? undefined : projectName(a.projectId)} onOpenItem={onOpenItem} />)}
      {tab === 'backlog' && (
        <>
          {state.items.length > 0 && view === 'doc' ? null : state.items.length > 0 ? (
            <FilterBar
              filter={filter}
              onChange={setFilter}
              view={view}
              labels={labels}
              owners={owners}
              views={views}
              activeViewId={home.savedId}
              onApplyView={(v) => setParams(viewParams(v, params), { replace: true })}
              searchRef={searchRef}
              builtinViews={builtinViews}
              leading={tabSwitch}
              trailing={
                <>
                  {view === 'board' && (
                    <Segmented
                      label="Board lanes"
                      value={lanes ? 'epic' : 'none'}
                      onChange={(v) => setParam('lanes', v === 'none' ? 'none' : null)}
                      options={[
                        { value: 'epic', label: 'By epic' },
                        { value: 'none', label: 'One board' },
                      ]}
                    />
                  )}
                  {view === 'defects' && (
                    <Segmented
                      label="Which defects"
                      value={home.showClosed ? 'all' : 'open'}
                      onChange={(v) => setParam('closed', v === 'all' ? '1' : null)}
                      options={[
                        { value: 'open', label: 'Open' },
                        { value: 'all', label: 'All' },
                      ]}
                    />
                  )}
                  {narrowed && <span className="text-caption text-fg-subtle num">{plural(matched.length, 'item')}</span>}
                </>
              }
              end={
                <>
                  {projectMenu}
                  {viewSwitch}
                </>
              }
            />
          ) : (
            plainBar
          )}
          {backlog}
        </>
      )}

      {tab === 'missions' && (
        <MissionList
          rows={missionRows}
          now={now}
          projectName={projectName}
          showProject={showProject}
          onOpen={(id) => navigate(`/missions/${encodeURIComponent(id)}`)}
          leading={tabSwitch}
          trailing={projectMenu}
          showFinished={scope === 'closed'}
          empty={
            <EmptyState
              icon={<Rocket />}
              title="No missions yet"
              description="Start one from any story, task or defect, or from scratch."
              action={
                projects.length ? (
                  <Button variant="primary" icon={<Rocket />} onClick={onNewMission}>
                    New mission
                  </Button>
                ) : (
                  <Button variant="primary" onClick={() => navigate(firstStep.to)}>
                    {hasMachine ? 'Add a project first' : 'Connect a machine'}
                  </Button>
                )
              }
            />
          }
        />
      )}

      {tab === 'review' && (
        <>
          {plainBar}
          <ReviewMissions queue={queue} projectName={projectName} showProject={showProject} onOpen={(id) => navigate(`/missions/${encodeURIComponent(id)}`)} />
          <Pressable
            aria-expanded={aiOpen}
            onClick={() => setShowAi(!aiOpen)}
            className="flex h-row w-full items-center gap-2 rounded-control px-row-x text-left transition-colors duration-(--duration-fast) hover:bg-hover"
          >
            <GroupChevron open={aiOpen} />
            <Sparkles className="size-4 shrink-0 text-fg-subtle" aria-hidden />
            {/* Short enough for a phone: proposals and suggested defects, in two words. */}
            <span className="truncate text-ui font-medium text-fg" title="AI proposals and suggested defects">
              AI suggestions
            </span>
            {aiToReview > 0 && <span className="text-caption text-fg-subtle num">{aiToReview}</span>}
          </Pressable>
          {aiOpen && <ReviewQueue proposals={state.proposals ?? []} signals={state.signals ?? []} jobs={jobs} projectId={projectId} projectName={projectName} onReview={onReview} onOpenItem={onOpenItem} onAiStarted={onAiStarted} />}
        </>
      )}
    </div>
  );
}
